import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { vars } from '@/lib/motion'

type WashStrength = 'strong' | 'medium' | 'light'

interface WashProps {
    strength?: WashStrength
    /** How far the wash fades in and out at the section's top and bottom, in px. */
    fade?: number
    className?: string
}

/**
 * A full-bleed sky wash behind a section. It is painted against the viewport, so it stays put
 * while the page scrolls past, and it feathers in and out at the section's edges.
 * Put it as the first child of a `relative isolate` section.
 */
export const Wash: FC<WashProps> = ({ strength = 'strong', fade, className }): ReactNode => {
    return (
        <div
            aria-hidden='true'
            className={cn('wash', `wash-${strength}`, className)}
            style={fade ? vars({ '--wash-fade': `${fade}px` }) : undefined}
        />
    )
}

interface AmbientProps {
    /** Paint the sky gradient inside the field too (for heroes without their own field). */
    field?: boolean
    className?: string
}

/** The breathing hero field: three huge soft blobs drifting on long loops over a faint dot grid. */
export const Ambient: FC<AmbientProps> = ({ field = false, className }): ReactNode => {
    return (
        <div aria-hidden='true' className={cn('ambient', field && 'ambient-field', className)}>
            <i />
            <i />
            <i />
        </div>
    )
}

interface StageWashProps {
    className?: string
}

/** A contained sky field that dissolves into the cream on every side. */
export const StageWash: FC<StageWashProps> = ({ className }): ReactNode => {
    return (
        <div
            aria-hidden='true'
            className={cn(
                'pointer-events-none absolute -inset-6 -z-10',
                'bg-[radial-gradient(ellipse_90%_60%_at_50%_55%,rgb(111_155_209/0.08)_0%,rgb(111_155_209/0.03)_45%,rgb(111_155_209/0)_70%),linear-gradient(180deg,var(--background)_0%,color-mix(in_srgb,#c9dcf0_14%,var(--background))_24%,color-mix(in_srgb,#c9dcf0_34%,var(--background))_52%,color-mix(in_srgb,#c9dcf0_14%,var(--background))_80%,var(--background)_100%)]',
                '[mask-composite:intersect] [mask-image:linear-gradient(90deg,transparent_0%,#000_14%,#000_86%,transparent_100%),linear-gradient(180deg,transparent_0%,#000_16%,#000_84%,transparent_100%)]',
                '[-webkit-mask-composite:source-in] [-webkit-mask-image:linear-gradient(90deg,transparent_0%,#000_14%,#000_86%,transparent_100%),linear-gradient(180deg,transparent_0%,#000_16%,#000_84%,transparent_100%)]',
                className
            )}
        >
            <div className='absolute inset-0 opacity-[0.16] [background-image:radial-gradient(rgb(40_38_34/0.11)_1px,transparent_1.4px)] [background-size:7px_7px]' />
        </div>
    )
}
